import { useState, useEffect, useCallback } from 'react';
import { Play, Pause, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import type { Contraction, ContractionStats, ContractionTrend } from '../types';
import { formatTimeMMSS, formatIntervalReadable } from '../utils/formatTime';
import { getTrendColorClass } from '../utils/calculateStats';

interface TimerCardProps {
  activeContraction: Contraction | null;
  stats: ContractionStats;
  timeSinceLastContraction: number | null;
  isLoading: boolean;
  onStart: () => Promise<void>;
  onStop: () => Promise<void>;
}

function getTrendIcon(trend: ContractionTrend) {
  switch (trend) {
    case 'decreasing':
      return <TrendingDown className="w-4 h-4" />;
    case 'increasing':
      return <TrendingUp className="w-4 h-4" />;
    case 'stable':
    default:
      return <Minus className="w-4 h-4" />;
  }
}

export function TimerCard({
  activeContraction,
  stats,
  timeSinceLastContraction,
  isLoading,
  onStart,
  onStop,
}: TimerCardProps) {
  const { t } = useTranslation('contractionTimer');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timeSinceLast, setTimeSinceLast] = useState(timeSinceLastContraction ?? 0);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const isActive = activeContraction !== null;

  // Timer for active contraction
  useEffect(() => {
    if (!activeContraction) {
      setElapsedSeconds(0);
      return;
    }

    // Calculate initial elapsed time
    const startTime = new Date(activeContraction.start_time).getTime();
    const now = Date.now();
    setElapsedSeconds(Math.floor((now - startTime) / 1000));

    // Update every second
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedSeconds(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeContraction]);

  // Timer for time since last contraction
  useEffect(() => {
    if (activeContraction) {
      setTimeSinceLast(0);
      return;
    }

    if (timeSinceLastContraction === null) {
      setTimeSinceLast(0);
      return;
    }

    setTimeSinceLast(timeSinceLastContraction);

    const interval = setInterval(() => {
      setTimeSinceLast((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeContraction, timeSinceLastContraction]);

  const handleButtonClick = useCallback(async () => {
    if (isActionLoading) return;

    setIsActionLoading(true);
    try {
      if (isActive) {
        await onStop();
      } else {
        await onStart();
      }
    } finally {
      setIsActionLoading(false);
    }
  }, [isActive, isActionLoading, onStart, onStop]);

  // Format display values
  const displayTime = isActive ? elapsedSeconds : timeSinceLast;
  const displayLabel = isActive ? t('timer.contractionRunning') : t('timer.sinceLast');

  return (
    <div
      className={cn(
        'rounded-[15px] p-4 transition-all duration-300',
        isActive
          ? 'bg-pp-orange text-white'
          : ''
      )}
    >
      {/* Main Timer Display */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p
            className={cn(
              'text-sm font-medium mb-1',
              isActive ? 'text-white/80' : 'text-white/70'
            )}
          >
            {displayLabel}
          </p>
          <div
            className={cn(
              'text-5xl font-bold tabular-nums tracking-tight',
              isActive ? 'text-white' : 'text-white'
            )}
          >
            {formatTimeMMSS(displayTime)}
          </div>
        </div>

        {/* Start/Stop Button */}
        <button
          onClick={handleButtonClick}
          disabled={isLoading || isActionLoading}
          className={cn(
            'w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-lg active:scale-95 disabled:opacity-50',
            isActive
              ? 'bg-white text-pp-orange hover:bg-gray-100'
              : 'bg-pp-orange text-white hover:bg-orange-600'
          )}
          aria-label={isActive ? t('timer.stopLabel') : t('timer.startLabel')}
        >
          {isActionLoading ? (
            <div className="w-8 h-8 border-3 border-current border-t-transparent rounded-full animate-spin" />
          ) : isActive ? (
            <Pause className="w-10 h-10" fill="currentColor" />
          ) : (
            <Play className="w-10 h-10 ml-1" fill="currentColor" />
          )}
        </button>
      </div>

      {/* Quick Stats */}
      <div
        className={cn(
          'mt-4 pt-4 border-t grid grid-cols-4 gap-2',
          isActive ? 'border-white/20' : 'border-white/20'
        )}
      >
        {/* Count */}
        <div className="text-center">
          <p className="text-2xl font-bold tabular-nums text-white">
            {stats.count}
          </p>
          <p className="text-xs text-white/70">
            {t('timer.contractions')}
          </p>
        </div>

        {/* Average Duration */}
        <div className="text-center">
          <p className="text-2xl font-bold tabular-nums text-white">
            {stats.avgDurationSeconds !== null
              ? `${Math.round(stats.avgDurationSeconds)}s`
              : '-'}
          </p>
          <p className="text-xs text-white/70">
            {t('timer.avgDuration')}
          </p>
        </div>

        {/* Average Interval */}
        <div className="text-center">
          <p className="text-2xl font-bold tabular-nums text-white">
            {stats.avgIntervalSeconds !== null
              ? formatIntervalReadable(stats.avgIntervalSeconds)
              : '-'}
          </p>
          <p className="text-xs text-white/70">
            {t('timer.avgInterval')}
          </p>
        </div>

        {/* Trend */}
        <div className="text-center">
          <div
            className={cn(
              'text-2xl font-bold flex items-center justify-center',
              isActive ? 'text-white' : getTrendColorClass(stats.trend)
            )}
          >
            {stats.count >= 3 ? getTrendIcon(stats.trend) : '-'}
          </div>
          <p className="text-xs text-white/70">
            {t('timer.trend')}
          </p>
        </div>
      </div>
    </div>
  );
}
